import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Zap, Target, Award } from 'lucide-react';

export default function ArchiveConfirmDialog({ 
  open, 
  onOpenChange, 
  onConfirm, 
  itemType, // 'mission', 'objective', 'activity', 'task'
  itemTitle 
}) {
  const getWarningMessage = () => {
    switch(itemType) {
      case 'mission':
        return 'Archiving this mission will result in aborting all associated objectives, activities, and tasks. All XP/EP/SP rewards for incomplete work will be lost.';
      case 'objective':
        return 'Archiving this objective will result in aborting all associated activities and tasks. All XP/EP/SP rewards for incomplete work will be lost.';
      case 'activity':
        return 'Archiving this activity will result in aborting all associated tasks. All XP/EP/SP rewards for incomplete work will be lost.';
      case 'task':
        return 'Archiving this task will result in the loss of any XP/EP/SP that would have been earned upon completion.';
      default:
        return 'Archiving this item will result in the loss of associated rewards.';
    }
  };

  const getPointsInfo = () => {
    switch(itemType) {
      case 'mission':
      case 'objective':
      case 'activity':
        return [
          { icon: Target, label: 'XP from direct tasks', color: 'text-amber-400' },
          { icon: Zap, label: 'Pending EP from indirect tasks', color: 'text-purple-400' },
          { icon: Award, label: 'SP from skill development', color: 'text-emerald-400' }
        ];
      case 'task':
        return [
          { icon: Target, label: 'XP/EP from completion', color: 'text-amber-400' },
          { icon: Award, label: 'SP from skill rewards', color: 'text-emerald-400' }
        ];
      default:
        return [];
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-400">
            <AlertTriangle className="w-5 h-5" />
            Archive {itemType?.charAt(0).toUpperCase() + itemType?.slice(1)}?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-slate-300 space-y-4">
            <p className="text-sm leading-relaxed">
              {getWarningMessage()}
            </p>
            
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 space-y-2">
              <div className="font-semibold text-rose-300 text-sm mb-2">Points Will Be Lost:</div>
              {getPointsInfo().map((point, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs">
                  <point.icon className={`w-3.5 h-3.5 ${point.color}`} />
                  <span className="text-slate-400">{point.label}</span>
                </div>
              ))}
            </div>

            <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-3">
              <p className="text-xs text-indigo-300">
                ℹ️ Archived items are removed from active lists but visible in archives. They can be reactivated or continued later by unarchiving.
              </p>
            </div>

            {itemTitle && (
              <p className="text-sm text-slate-500 italic">
                Item: "{itemTitle}"
              </p>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-100 hover:bg-slate-700">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={onConfirm}
            className="bg-rose-600 hover:bg-rose-700 text-white"
          >
            Yes, Archive It
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}